import ImageIcon from "@redotech/redo-icons/image-01.svg";
import PaperclipIcon from "@redotech/redo-icons/paperclip.svg";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useCallback, useRef } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

// Detached <input> so it outlives the overflow dropdown, which unmounts on focus
// loss to the OS picker before a JSX-rendered input's `change` lands (PRO-1105).
function openFilePicker({
  accept,
  multiple,
  onFiles,
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
}): void {
  const input = document.createElement("input");
  input.type = "file";
  if (accept) {
    input.accept = accept;
  }
  input.multiple = multiple ?? false;
  input.onchange = () => {
    const files = input.files ? [...input.files] : [];
    if (files.length > 0) {
      onFiles(files);
    }
  };
  input.click();
}

interface ImageToolbarButtonProps {
  disabled?: boolean;
  onImageSelect?: (file: File) => void;
}

export const ImageToolbarButton = memo(function ImageToolbarButton({
  disabled,
  onImageSelect,
}: ImageToolbarButtonProps) {
  const { isOverflow } = useToolbarOverflow();

  // Opening the picker steals focus and clears editor.selection; track the last
  // non-null selection so we can restore it before inserting the image.
  const editor = useEditorRef();
  const savedSelectionRef = useRef<typeof editor.selection>(null);
  useEditorSelector((editor) => {
    if (editor.selection) {
      savedSelectionRef.current = editor.selection;
    }
    return null;
  }, []);

  const openPicker = useCallback(() => {
    openFilePicker({
      accept: "image/*",
      onFiles: (files) => {
        const file = files[0];
        if (!file) {
          return;
        }
        const saved = editor.selection ?? savedSelectionRef.current;
        if (saved) {
          editor.tf.select(saved);
          editor.tf.focus();
        }
        onImageSelect?.(file);
      },
    });
  }, [editor, onImageSelect]);

  return isOverflow ? (
    <DropdownMenuItem disabled={disabled} onSelect={() => openPicker()}>
      <RedoIcon Icon={ImageIcon} size={16} />
      Image
    </DropdownMenuItem>
  ) : (
    <RedoButton
      aria-label="Insert image"
      disabled={disabled}
      hierarchy="tertiary"
      IconLeading={ImageIcon}
      onClick={openPicker}
      size="sm"
    />
  );
});

interface FileToolbarButtonProps {
  disabled?: boolean;
  onFileSelect?: (files: File[]) => void;
}

export const FileToolbarButton = memo(function FileToolbarButton({
  disabled,
  onFileSelect,
}: FileToolbarButtonProps) {
  const { isOverflow } = useToolbarOverflow();

  const openPicker = useCallback(() => {
    openFilePicker({
      multiple: true,
      onFiles: (files) => onFileSelect?.(files),
    });
  }, [onFileSelect]);

  return isOverflow ? (
    <DropdownMenuItem disabled={disabled} onSelect={() => openPicker()}>
      <RedoIcon Icon={PaperclipIcon} size={16} />
      Attach file
    </DropdownMenuItem>
  ) : (
    <RedoButton
      aria-label="Attach file"
      disabled={disabled}
      hierarchy="tertiary"
      IconLeading={PaperclipIcon}
      onClick={openPicker}
      size="sm"
    />
  );
});
